import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { styled } from "styled-components";
import { useFleetAuth } from "../../auth/fleetAuth";
import { Content, FullPageContainer, Right } from "../Dashboard/Dashboard";
import Navbar from "../../components/Navbar/Navbar";
import PageHeader from "../../components/PageHeader/PageHeader";
import Footer from "../../components/Footer/Footer";
import ViewSwitcher from "../../components/ViewSwitcher/ViewSwitcher";
import {
  AirlineCode,
  AutoRefreshGroup,
  ChangeDot,
  ChipContainer,
  ColumnDef,
  ConcourseChip,
  ConcourseChipRow,
  CountBadge,
  DOT_COLORS,
  EmptyMessage,
  ErrorBanner,
  FilterCard,
  FilterCheckbox,
  FilterForm,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FlightCell,
  FlightTableRow,
  HighlightCell,
  LoadingText,
  MonoText,
  RelativeText,
  SearchButton,
  SortDir,
  SortableHeaderCell,
  Table,
  TableBody,
  TableHeader,
  TableScrollWrapper,
  TableTitle,
  TimeTypeBadge,
  AIRPORT_GATE_GROUPS,
  formatDateTime,
  formatTime,
  normalizeGate,
  parsePierFilter,
  relativeTime,
  timeTypeLabel,
  useColumnVisibility,
  useSort,
} from "../../components/FlightDataTable";
import type { HighlightKind } from "../../components/FlightDataTable";
import { fetchNexusSnapshots } from "./api";
import {
  KeyQuality,
  NexusFilters,
  NexusSnapshot,
  NexusSnapshotsResponse,
} from "./types";

const AUTO_REFRESH_MS = 60_000;
const ONE_HOUR_MS = 60 * 60 * 1000;

// Nexus is a Delta-only feed; if al_cde is missing on a partial-key row we
// still want the airline column to read "DL" so the table mirrors Flights.
const DEFAULT_AL_CDE = "DL";

function allConcourseLabels(airport: string): ReadonlySet<string> {
  const groups = AIRPORT_GATE_GROUPS[airport.toUpperCase()] ?? [];
  return new Set(groups.map((g) => g.label));
}

const DEFAULT_FILTERS: NexusFilters = {
  airport: "ATL",
  hoursBack: 0,
  hoursForward: 9,
  selectedConcourses: allConcourseLabels("ATL"),
  // Mirrors the Flights page default — most operationally interesting
  // piers at ATL fall in this range. Empty string = show all.
  pierFilter: "40-60",
};

type ColumnKey =
  | "flight"
  | "destination"
  | "date"
  | "time"
  | "timeType"
  | "relative"
  | "gate"
  | "bagPier"
  | "aircraft"
  | "pax"
  | "boarded"
  | "loaded"
  | "observed"
  | "stat"
  | "keyQuality";

type SortKey =
  | "flight"
  | "destination"
  | "time"
  | "timeType"
  | "gate"
  | "bagPier"
  | "aircraft"
  | "boarded"
  | "loaded"
  | "observed";

const ALL_COLUMNS: readonly ColumnDef<ColumnKey, SortKey>[] = [
  { key: "flight", label: "Flight", sortKey: "flight" },
  // Toggle-chip label is "Destination" so the chip rows match Flights.
  // The visible header gets relabeled to "To" below — Nexus is always
  // outbound-from-origin, mirroring Flights' outbound table.
  { key: "destination", label: "Destination", sortKey: "destination" },
  { key: "date", label: "Date", sortKey: "time" },
  { key: "time", label: "Time", sortKey: "time" },
  { key: "timeType", label: "Status", sortKey: "timeType" },
  { key: "relative", label: "Relative", sortKey: null },
  { key: "gate", label: "Gate", sortKey: "gate" },
  { key: "bagPier", label: "Pier", sortKey: "bagPier" },
  { key: "aircraft", label: "Aircraft", sortKey: "aircraft" },
  { key: "pax", label: "Lcl/Xfer PAX", sortKey: null },
  { key: "boarded", label: "% Boarded", sortKey: "boarded" },
  { key: "loaded", label: "% Loaded", sortKey: "loaded" },
  { key: "observed", label: "Observed", sortKey: "observed" },
  { key: "stat", label: "Stat", sortKey: null },
  { key: "keyQuality", label: "Key", sortKey: null },
];

// Mirror the Flights page default-visible set so the two tables look
// the same on first load. Nexus-specific stats (% Boarded, % Loaded,
// Observed, Stat, Key) stay opt-in via the column toggle row.
const DEFAULT_VISIBLE_COLUMNS: ReadonlySet<ColumnKey> = new Set<ColumnKey>([
  "flight",
  "destination",
  "time",
  "timeType",
  "relative",
  "gate",
  "bagPier",
]);

function fmtPct(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${Math.round(v)}%`;
}

function fmtNum(v: number | null | undefined): string {
  return v == null ? "—" : String(v);
}

function fmtObservedAt(ms: number): string {
  return new Date(ms).toISOString().slice(11, 16) + "Z";
}

function getFieldValue(s: NexusSnapshot, key: SortKey): string | number {
  switch (key) {
    case "flight":
      return `${s.al_cde ?? DEFAULT_AL_CDE}${String(s.flt_num).padStart(6, "0")}`;
    case "destination":
      return s.leg_dest_ap_cde ?? "ZZZ";
    case "time":
      return s.mission_time ?? Number.MAX_SAFE_INTEGER;
    case "timeType":
      // A < E < S so actuals sort before estimates before schedules.
      return s.time_type ?? "Z";
    case "gate":
      return s.dptr_gate ?? "";
    case "bagPier":
      return s.dptr_bag_pier_num ?? "";
    case "aircraft":
      return s.ac_reg_num ?? "";
    case "boarded":
      return s.pct_boarded ?? -1;
    case "loaded":
      return s.pct_loaded ?? -1;
    case "observed":
      return s.observed_at;
  }
}

function sortSnapshots(
  snapshots: readonly NexusSnapshot[],
  key: SortKey,
  dir: SortDir,
): NexusSnapshot[] {
  return [...snapshots].sort((a, b) => {
    const av = getFieldValue(a, key);
    const bv = getFieldValue(b, key);
    const cmp =
      typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv));
    return dir === "asc" ? cmp : -cmp;
  });
}

const Nexus = () => {
  const { getAccessToken } = useFleetAuth();
  const [filters, setFilters] = useState<NexusFilters>(DEFAULT_FILTERS);
  const [draft, setDraft] = useState<NexusFilters>(DEFAULT_FILTERS);
  const [data, setData] = useState<NexusSnapshotsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const sort = useSort<SortKey>("time");
  const { isVisible, toggle: handleColumnToggle } =
    useColumnVisibility<ColumnKey>(DEFAULT_VISIBLE_COLUMNS);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(
    async (f: NexusFilters) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const result = await fetchNexusSnapshots(f, token, controller.signal);
        if (!controller.signal.aborted) setData(result);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [getAccessToken],
  );

  useEffect(() => {
    load(filters);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(() => load(filters), AUTO_REFRESH_MS);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, filters, load]);

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    // Airport change resets the concourse selection to "all chips on"
    // for the new airport, mirroring the Flights page behaviour.
    const airportChanged = draft.airport !== filters.airport;
    const next: NexusFilters = airportChanged
      ? { ...draft, selectedConcourses: allConcourseLabels(draft.airport) }
      : draft;
    setFilters(next);
    setDraft(next);
    load(next);
  };

  const updateDraft = <K extends keyof NexusFilters>(
    key: K,
    value: NexusFilters[K],
  ) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleConcourseToggle = (label: string) => {
    const toggle = (prev: NexusFilters): NexusFilters => {
      const next = new Set(prev.selectedConcourses);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return { ...prev, selectedConcourses: next };
    };
    setFilters(toggle);
    setDraft(toggle);
  };

  const currentGateGroups = useMemo(
    () => AIRPORT_GATE_GROUPS[filters.airport.toUpperCase()] ?? [],
    [filters.airport],
  );

  const allowedGates = useMemo(() => {
    if (currentGateGroups.length === 0) return null; // no filter
    if (filters.selectedConcourses.size === 0) return new Set<string>();
    const gates = new Set<string>();
    for (const group of currentGateGroups) {
      if (filters.selectedConcourses.has(group.label)) {
        for (const gate of group.gates) gates.add(gate);
      }
    }
    return gates;
  }, [currentGateGroups, filters.selectedConcourses]);

  const allowedPiers = useMemo(
    () => parsePierFilter(filters.pierFilter),
    [filters.pierFilter],
  );

  const filtered = useMemo(() => {
    const all = data?.snapshots ?? [];
    return all.filter((s) => {
      if (
        allowedGates != null &&
        !allowedGates.has(normalizeGate((s.dptr_gate ?? "").toUpperCase()))
      ) {
        return false;
      }
      if (
        allowedPiers.size > 0 &&
        !allowedPiers.has(s.dptr_bag_pier_num ?? "")
      ) {
        return false;
      }
      return true;
    });
  }, [data, allowedGates, allowedPiers]);

  const sorted = useMemo(
    () => sortSnapshots(filtered, sort.sortKey, sort.sortDir),
    [filtered, sort.sortKey, sort.sortDir],
  );

  // Match Flights' outbound table: chip says "Destination", header
  // shows "To". Nexus is always outbound from origin so the rename is
  // unconditional here.
  const activeColumns = ALL_COLUMNS.filter((col) => isVisible(col.key)).map(
    (col) => (col.key === "destination" ? { ...col, label: "To" } : col),
  );

  const now = Date.now();

  // 1-hour freshness window mirrors the Flights page — fields whose
  // value last changed within this window get highlighted. The lambda
  // stamps `field_updated_at` per field so we don't need access to
  // prior snapshots; one row carries all the information we need.
  const fieldUpdatedWithin = (
    s: NexusSnapshot,
    fieldName: string,
    windowMs: number,
  ): boolean => {
    const ts = s.field_updated_at?.[fieldName];
    if (ts == null) return false;
    return ts > now - windowMs;
  };

  const renderRow = (s: NexusSnapshot) => {
    const ms = s.mission_time ?? null;
    // Prefer mission_time for "past" detection so the row dims as soon
    // as scheduled time slips behind now, matching Flights. Fall back
    // to the actual_out / stat heuristic for rows the lambda couldn't
    // resolve (no time set on the row).
    const isPast =
      ms != null ? ms < now : Boolean(s.actual_out) || s.stat === "OUT";
    const alCde = s.al_cde ?? DEFAULT_AL_CDE;

    // Track freshness against the same field that drove mission_time
    // so the highlight matches what the user is reading in the Time cell.
    const timeField =
      s.time_type === "A"
        ? "actual_out"
        : s.time_type === "E"
          ? "est_out"
          : s.time_type === "S"
            ? "sked_out"
            : null;
    const timeChanged =
      timeField != null && fieldUpdatedWithin(s, timeField, ONE_HOUR_MS);
    const gateChanged = fieldUpdatedWithin(s, "dptr_gate", ONE_HOUR_MS);
    const pierChanged = fieldUpdatedWithin(s, "dptr_bag_pier_num", ONE_HOUR_MS);

    const changeDots: Exclude<HighlightKind, "none">[] = [];
    if (timeChanged) changeDots.push("time");
    if (gateChanged) changeDots.push("gate");

    return (
      <FlightTableRow key={s.SK} $isPast={isPast}>
        {isVisible("flight") && (
          <FlightCell>
            <AirlineCode>{alCde}</AirlineCode>
            {s.flt_num}
            {changeDots.map((kind) => (
              <ChangeDot key={kind} $color={DOT_COLORS[kind]} />
            ))}
          </FlightCell>
        )}
        {isVisible("destination") && (
          <FlightCell>
            <strong>{s.leg_dest_ap_cde ?? "—"}</strong>
          </FlightCell>
        )}
        {isVisible("date") && (
          <FlightCell>{ms != null ? formatDateTime(ms) : "—"}</FlightCell>
        )}
        {isVisible("time") && (
          <HighlightCell $highlight={timeChanged ? "time" : "none"}>
            {ms != null ? formatTime(ms) : "—"}
          </HighlightCell>
        )}
        {isVisible("timeType") && (
          <FlightCell>
            {s.time_type ? (
              <TimeTypeBadge $type={s.time_type}>
                {timeTypeLabel(s.time_type)}
              </TimeTypeBadge>
            ) : (
              <span>—</span>
            )}
          </FlightCell>
        )}
        {isVisible("relative") && (
          <FlightCell>
            <RelativeText $isPast={isPast}>
              {ms != null ? relativeTime(ms) : "—"}
            </RelativeText>
          </FlightCell>
        )}
        {isVisible("gate") && (
          <HighlightCell $highlight={gateChanged ? "gate" : "none"}>
            {s.dptr_gate ?? "—"}
          </HighlightCell>
        )}
        {isVisible("bagPier") && (
          <HighlightCell $highlight={pierChanged ? "gate" : "none"}>
            {s.dptr_bag_pier_num ?? "—"}
          </HighlightCell>
        )}
        {isVisible("aircraft") && (
          <FlightCell>
            <MonoText>{s.ac_reg_num ?? "—"}</MonoText>
          </FlightCell>
        )}
        {isVisible("pax") && (
          <FlightCell>
            {fmtNum(s.local_pax)} / {fmtNum(s.xfer_pax)}
          </FlightCell>
        )}
        {isVisible("boarded") && (
          <FlightCell>{fmtPct(s.pct_boarded)}</FlightCell>
        )}
        {isVisible("loaded") && <FlightCell>{fmtPct(s.pct_loaded)}</FlightCell>}
        {isVisible("observed") && (
          <FlightCell>
            <MonoText>{fmtObservedAt(s.observed_at)}</MonoText>
          </FlightCell>
        )}
        {isVisible("stat") && (
          <FlightCell>
            {s.stat ? (
              <StatBadge $stat={s.stat}>{s.stat}</StatBadge>
            ) : (
              <span>—</span>
            )}
          </FlightCell>
        )}
        {isVisible("keyQuality") && (
          <FlightCell>
            <KeyBadge $quality={s.key_quality}>{s.key_quality}</KeyBadge>
          </FlightCell>
        )}
      </FlightTableRow>
    );
  };

  return (
    <FullPageContainer>
      <Navbar />
      <Right>
        <PageHeader>Nexus Flight Snapshots</PageHeader>
        <Content>
          <ViewSwitcher />
          {/* Filters */}
          <FilterCard>
            <FilterForm onSubmit={handleApply}>
              <FilterGroup>
                <FilterLabel htmlFor="airport">Airport</FilterLabel>
                <FilterInput
                  id="airport"
                  value={draft.airport}
                  onChange={(e) =>
                    updateDraft("airport", e.target.value.toUpperCase())
                  }
                  placeholder="ATL"
                  maxLength={4}
                  required
                  style={{ width: "80px" }}
                />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel htmlFor="hoursBack">Past Hours</FilterLabel>
                <FilterInput
                  id="hoursBack"
                  type="number"
                  min={0}
                  max={48}
                  value={draft.hoursBack}
                  onChange={(e) =>
                    updateDraft("hoursBack", Number(e.target.value))
                  }
                  style={{ width: "80px" }}
                />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel htmlFor="hoursForward">Hours Fwd</FilterLabel>
                <FilterInput
                  id="hoursForward"
                  type="number"
                  min={0}
                  max={48}
                  value={draft.hoursForward}
                  onChange={(e) =>
                    updateDraft("hoursForward", Number(e.target.value))
                  }
                  style={{ width: "80px" }}
                />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel htmlFor="pierFilter">Piers</FilterLabel>
                <FilterInput
                  id="pierFilter"
                  value={draft.pierFilter}
                  onChange={(e) => updateDraft("pierFilter", e.target.value)}
                  placeholder="e.g. 40-60, 72"
                  style={{ width: "130px" }}
                />
              </FilterGroup>
              <SearchButton
                type="submit"
                disabled={loading || !draft.airport.trim()}
              >
                {loading ? "Loading..." : "Search"}
              </SearchButton>
              <AutoRefreshGroup>
                <FilterCheckbox
                  id="autoRefresh"
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                <FilterLabel htmlFor="autoRefresh" style={{ marginBottom: 0 }}>
                  Auto-refresh
                </FilterLabel>
              </AutoRefreshGroup>
            </FilterForm>

            {/* Concourse Chips */}
            {currentGateGroups.length > 0 && (
              <ConcourseChipRow>
                <FilterLabel as="span">Concourse</FilterLabel>
                <ChipContainer>
                  {currentGateGroups.map((group) => (
                    <ConcourseChip
                      key={group.label}
                      type="button"
                      $active={filters.selectedConcourses.has(group.label)}
                      onClick={() => handleConcourseToggle(group.label)}
                    >
                      {group.label}
                    </ConcourseChip>
                  ))}
                </ChipContainer>
              </ConcourseChipRow>
            )}

            {/* Column Toggles */}
            <ConcourseChipRow>
              <FilterLabel as="span">Columns</FilterLabel>
              <ChipContainer>
                {ALL_COLUMNS.map((col) => (
                  <ConcourseChip
                    key={col.key}
                    type="button"
                    $active={isVisible(col.key)}
                    onClick={() => handleColumnToggle(col.key)}
                  >
                    {col.label}
                  </ConcourseChip>
                ))}
              </ChipContainer>
            </ConcourseChipRow>
          </FilterCard>

          {error && <ErrorBanner>{error}</ErrorBanner>}
          {loading && !data && <LoadingText>Loading snapshots...</LoadingText>}

          {data && (
            <Table>
              <TableTitle>
                Outbound Flights
                <CountBadge>{sorted.length}</CountBadge>
              </TableTitle>
              {sorted.length === 0 ? (
                <EmptyMessage>No outbound flights found.</EmptyMessage>
              ) : (
                <TableScrollWrapper>
                  <TableBody $minWidth={900}>
                    <TableHeader>
                      {activeColumns.map((col) => (
                        <SortableHeaderCell
                          key={col.key}
                          $sortable={col.sortKey !== null}
                          onClick={
                            col.sortKey
                              ? () => sort.setSort(col.sortKey!)
                              : undefined
                          }
                        >
                          {col.label}
                          {sort.indicator(col)}
                        </SortableHeaderCell>
                      ))}
                    </TableHeader>
                    {sorted.map(renderRow)}
                  </TableBody>
                </TableScrollWrapper>
              )}
            </Table>
          )}
        </Content>
        <Footer />
      </Right>
    </FullPageContainer>
  );
};

/* ── Page-specific styled components ──────────────────────────────────
 * Generic chrome (FilterCard, Table, FlightTableRow, TimeTypeBadge,
 * HighlightCell, ChangeDot…) lives in src/components/FlightDataTable.
 * Only the Nexus-specific Stat / Key badges stay here.
 * ─────────────────────────────────────────────────────────────────── */

const StatBadge = styled.span<{ $stat: string }>`
  font-size: 10px;
  font-weight: ${(props) => props.theme.fontWeight.medium};
  padding: 1px 6px;
  border-radius: 3px;
  white-space: nowrap;
  background-color: ${(props) => {
    switch (props.$stat) {
      case "OUT":
        return "rgba(9, 168, 63, 0.12)";
      case "DLY":
        return "rgba(206, 38, 38, 0.12)";
      default:
        return "rgba(0, 0, 0, 0.06)";
    }
  }};
  color: ${(props) => {
    switch (props.$stat) {
      case "OUT":
        return props.theme.colors.green;
      case "DLY":
        return props.theme.colors.danger;
      default:
        return props.theme.colors.grey;
    }
  }};
`;

const KeyBadge = styled.span<{ $quality: KeyQuality }>`
  font-size: 10px;
  font-weight: ${(props) => props.theme.fontWeight.medium};
  padding: 1px 6px;
  border-radius: 3px;
  white-space: nowrap;
  background-color: ${(props) =>
    props.$quality === "full"
      ? "rgba(9, 168, 63, 0.12)"
      : "rgba(3, 148, 232, 0.12)"};
  color: ${(props) =>
    props.$quality === "full"
      ? props.theme.colors.green
      : props.theme.colors.blue};
`;

export default Nexus;
